import React, { useEffect, useState } from "react";
import { api, downloadApiFile, formatApiErrorDetail } from "@/lib/api";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCcw, FileText, Truck, Package, Scale, FileSpreadsheet } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import { fmtFecha } from "@/lib/fechas";
import { fmtEnvios } from "@/lib/textos";
import { toast } from "sonner";

const ESTADO = { generado: "Generado", modificado: "Modificado", anulado: "Anulado" };
const T_ENVIOS = "Env" + String.fromCharCode(0xed) + "os";

function fmtNum(n, decimals = 0) {
  return Number(n || 0).toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

const EMPTY = {
  total_decas: 0,
  total_envios: 0,
  total_bultos: 0,
  total_kg: 0,
  total_anulados: 0,
  por_estado: { generado: 0, modificado: 0, anulado: 0 },
  por_cargador: [],
  por_transportista: [],
  por_dia: [],
};

export default function InformesPage() {
  const [q, setQ] = useState({ desde: "", hasta: "" });
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.desde) params.desde = q.desde;
      if (q.hasta) params.hasta = q.hasta;
      const { data: body } = await api.get("/api/v1/informes", { params });
      setData({ ...EMPTY, ...body });
    } catch (_e) {
      setData(EMPTY);
    } finally {
      setLoading(false);
      setReady(true);
    }
  };

  useEffect(() => { load(); }, [q.desde, q.hasta]); // eslint-disable-line

  const exportXlsx = async () => {
    const params = {};
    if (q.desde) params.desde = q.desde;
    if (q.hasta) params.hasta = q.hasta;
    setExporting(true);
    try {
      await downloadApiFile("/api/v1/informes/export/xlsx", { params, fallbackName: "informe_deca.xlsx" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo descargar el archivo");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Informes"
        subtitle={"Totales de DeCA vigentes (sin anulados), " + T_ENVIOS.toLowerCase() + ", bultos y kg, con gr" + String.fromCharCode(0xe1) + "fico por periodo."}
        testid="page-informes"
        actions={<>
          <Button variant="outline" onClick={exportXlsx} disabled={exporting} data-testid="btn-informe-xlsx">
            <FileSpreadsheet className="h-4 w-4 mr-2"/> {exporting ? "Descargando" + String.fromCharCode(0x2026) : "Excel"}
          </Button>
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}/> Actualizar</Button>
        </>}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Desde">
              <DatePicker value={q.desde} onChange={(v)=>setQ({...q, desde:v})} testid="inf-desde"/>
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Hasta">
              <DatePicker value={q.hasta} onChange={(v)=>setQ({...q, hasta:v})} testid="inf-hasta"/>
            </Field>
          </div>
        </div>
      </Card>

      {ready ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-2">
            <Metric icon={FileText} label="DeCA vigentes" value={fmtNum(data.total_decas)} testid="inf-decas"/>
            <Metric icon={Package} label={T_ENVIOS} value={fmtNum(data.total_envios)} testid="inf-envios"/>
            <Metric icon={Package} label="Bultos" value={fmtNum(data.total_bultos)} testid="inf-bultos"/>
            <Metric icon={Scale} label="Kg" value={fmtNum(data.total_kg, 1)} testid="inf-kg"/>
            <Metric icon={Truck} label="Cargadores" value={fmtNum(data.por_cargador.length)}/>
            <Metric icon={Truck} label="Transportistas" value={fmtNum(data.por_transportista.length)}/>
          </div>
          <div className="text-xs text-slate-500 mb-4" data-testid="inf-anulados-nota">
            Los {fmtNum(data.total_anulados)} DeCA anulados no entran en los totales. Figuran en el desglose por estado y en el gr{String.fromCharCode(0xe1)}fico.
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-4">
            <PeriodoChart
              title={"DeCA por d" + String.fromCharCode(0xed) + "a"}
              puntos={data.por_dia}
              series={[
                { key: "generado", label: "Generados", color: "#10b981" },
                { key: "modificado", label: "Modificados", color: "#f59e0b" },
                { key: "anulado", label: "Anulados", color: "#ef4444" },
              ]}
              testid="chart-deca"
            />
            <PeriodoChart
              title={T_ENVIOS + ", bultos y kg"}
              puntos={data.por_dia}
              series={[
                { key: "envios", label: T_ENVIOS, color: "#0051C3" },
                { key: "bultos", label: "Bultos", color: "#0f172a" },
                { key: "kg", label: "Kg", color: "#64748b" },
              ]}
              testid="chart-carga"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Card className="p-4">
              <div className="label-tag mb-2">Por estado</div>
              {Object.entries(data.por_estado).map(([k, v]) => (
                <Row key={k} label={ESTADO[k] || k} value={fmtNum(v)}/>
              ))}
            </Card>
            <Card className="p-4">
              <div className="label-tag mb-2">Top cargadores</div>
              {data.por_cargador.slice(0, 10).map((c, i) => (
                <Row key={i} label={c.nombre} value={fmtNum(c.n)} extra={`${fmtEnvios(c.envios)} \u00b7 ${fmtNum(c.bultos)} bultos \u00b7 ${fmtNum(c.kg, 1)} kg`}/>
              ))}
              {data.por_cargador.length === 0 ? <div className="text-sm text-slate-500">Sin datos</div> : null}
            </Card>
            <Card className="p-4">
              <div className="label-tag mb-2">Top transportistas</div>
              {data.por_transportista.slice(0, 10).map((c, i) => (
                <Row key={i} label={c.nombre} value={fmtNum(c.n)} extra={`${fmtEnvios(c.envios)} \u00b7 ${fmtNum(c.bultos)} bultos \u00b7 ${fmtNum(c.kg, 1)} kg`}/>
              ))}
              {data.por_transportista.length === 0 ? <div className="text-sm text-slate-500">Sin datos</div> : null}
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-tag">{label}</div>
        <div className="h-8 w-8 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center"><Icon className="h-4 w-4 text-slate-700"/></div>
      </div>
      <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Chivo" }}>{value}</div>
    </Card>
  );
}

function Row({ label, value, extra }) {
  return (
    <div className="flex justify-between items-start gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm text-slate-900">{label}</div>
        {extra ? <div className="text-[11px] text-slate-500">{extra}</div> : null}
      </div>
      <div className="font-mono font-semibold shrink-0">{value}</div>
    </div>
  );
}

function PeriodoChart({ title, puntos, series, testid }) {
  const rows = puntos || [];
  if (!rows.length) {
    return (
      <Card className="p-4" data-testid={testid}>
        <div className="label-tag mb-2">{title}</div>
        <div className="text-sm text-slate-500 py-8 text-center">Sin datos en el periodo</div>
      </Card>
    );
  }
  const max = Math.max(...rows.flatMap((p) => series.map((s) => Number(p[s.key] || 0))), 1);
  const width = 560;
  const height = 220;
  const padL = 36, padR = 12, padT = 12, padB = 36;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;
  const groupW = chartW / rows.length;
  const barW = Math.max(3, (groupW * 0.7) / series.length);

  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="label-tag">{title}</div>
        <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }}/> {s.label}
            </span>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg width={width} height={height} className="text-slate-400">
          {[0, 0.5, 1].map((t, i) => (
            <g key={i}>
              <line x1={padL} x2={width - padR} y1={padT + chartH * (1 - t)} y2={padT + chartH * (1 - t)} stroke="currentColor" strokeOpacity="0.15"/>
              <text x={padL - 6} y={padT + chartH * (1 - t) + 3} textAnchor="end" fontSize="10" fill="currentColor">{Math.round(max * t)}</text>
            </g>
          ))}
          {rows.map((p, i) => {
            const gx = padL + i * groupW + groupW * 0.15;
            return (
              <g key={p.fecha || i}>
                {series.map((s, si) => {
                  const v = Number(p[s.key] || 0);
                  const h = (v / max) * chartH;
                  const x = gx + si * barW;
                  const y = padT + chartH - h;
                  return <rect key={s.key} x={x} y={y} width={barW - 1} height={Math.max(h, v ? 1 : 0)} fill={s.color} opacity="0.9"/>;
                })}
                {rows.length <= 16 ? (
                  <text x={padL + i * groupW + groupW / 2} y={height - 8} textAnchor="middle" fontSize="9" fill="#64748b">
                    {fmtFecha(p.fecha).slice(0, 5)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>
    </Card>
  );
}
